import {
  AlertTriangle,
  Activity,
  ArrowLeft,
  BadgeDollarSign,
  Bell,
  Building2,
  FileCheck2,
  FolderTree,
  LayoutDashboard,
  ShieldCheck,
  Users,
  ScrollText,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

export function AdminShell({ basePath = '/admin', marketplaceUrl }: { basePath?: string; marketplaceUrl?: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const initials = user?.name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'AD';

  const root = basePath || '/';
  const tab = (name: string) => `${root}?tab=${name}`;
  const adminNav = [
    { to: root, label: 'Operations overview', icon: LayoutDashboard, exact: true },
    { to: tab('analytics'), label: 'Analytics', icon: Activity },
    { to: tab('users'), label: 'Users & access', icon: Users },
    { to: tab('ownership'), label: 'Hostel ownership', icon: ShieldCheck },
    { to: tab('agencies'), label: 'Agency verification', icon: FileCheck2 },
    { to: tab('hostels'), label: 'Hostel catalogue', icon: Building2 },
    { to: tab('content'), label: 'Published content', icon: FolderTree },
    { to: tab('reports'), label: 'Reports & disputes', icon: AlertTriangle },
    { to: tab('campaigns'), label: 'Advertising', icon: BadgeDollarSign },
    { to: tab('audit'), label: 'Audit history', icon: ScrollText },
  ];

  return (
    <div className="workspace-shell">
      <aside className="workspace-sidebar">
        <div>
          <div className="flex items-center gap-3 px-2 py-1">
            <Logo compact />
            <span className="text-xs font-bold uppercase tracking-wider text-[var(--danger)] bg-[var(--danger-soft)] px-2 py-0.5 rounded-[var(--radius-pill)]">
              Admin
            </span>
          </div>

          <nav className="workspace-sidebar-nav" aria-label="Admin moderation navigation">
            {adminNav.map(({ to, label, icon: Icon, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                className={() => {
                  const tab = new URLSearchParams(to.split('?')[1] || '').get('tab');
                  const active = tab ? params.get('tab') === tab : !params.get('tab');
                  return `workspace-nav-item ${active ? 'active' : ''}`;
                }}
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="pt-4 border-t border-[var(--border-subtle)] flex flex-col gap-2">
          <button
            className="workspace-nav-item w-full justify-start text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            onClick={() => marketplaceUrl ? window.location.assign(marketplaceUrl) : navigate('/')}
          >
            <ArrowLeft size={18} />
            <span>View Marketplace</span>
          </button>
        </div>
      </aside>

      <div className="workspace-main">
        <header className="workspace-topbar">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[var(--radius-md)] bg-[var(--danger-soft)] text-[var(--danger)] font-bold text-xs flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-[var(--text-primary)]">Admin Control Center</h1>
              <p className="text-xs text-[var(--text-tertiary)]">Platform Moderation & Compliance</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              className="w-8 h-8 rounded-full bg-[var(--text-primary)] text-[var(--bg-surface)] flex items-center justify-center text-xs font-bold shrink-0"
              onClick={() => navigate('/settings')}
            >
              {initials}
            </button>
          </div>
        </header>

        <main className="workspace-content">
          <Outlet />
        </main>
      </div>
      <nav className="workspace-mobile-nav admin-mobile-nav" aria-label="Admin mobile navigation">
        {adminNav.map(({ to, label, icon: Icon }) => {
          const tab = new URLSearchParams(to.split('?')[1] || '').get('tab');
          const active = tab ? params.get('tab') === tab : !params.get('tab');
          return <NavLink to={to} key={to} title={label} className={active ? 'active' : ''}><Icon size={19}/><span>{label}</span></NavLink>;
        })}
      </nav>
    </div>
  );
}
